import React, { useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TableSortLabel,
    Chip,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';

export const columns = [
    { label: 'Status', key: 'status', sortable: true },
    { label: 'Batch Number', key: 'batch_number', sortable: true },
    { label: 'Product', key: 'product', sortable: true },
    { label: 'HM Purity', key: 'hm_purity', sortable: true },
    { label: 'Qty', key: 'qty', sortable: true, numeric: true },
    { label: 'BOM Dia Qty', key: 'bom_dia_qty', sortable: true, numeric: true },
    { label: 'BOM Clr Qty', key: 'bom_clr_qty', sortable: true, numeric: true },
    { label: 'Issue Tot Qty', key: 'issue_tot_qty', sortable: true, numeric: true },
    { label: 'Stn Wt/Gms', key: 'stone_weight', sortable: true, numeric: true },
    { label: 'Stn Wt/Cts', key: 'stone_weight_cts', sortable: true, numeric: true },
    { label: 'Department Name', key: 'department_name', sortable: true },
    { label: 'Worker Name', key: 'worker_name', sortable: true },
    { label: 'Received Wt', key: 'received_weight', sortable: true, numeric: true },
    { label: 'Issue Wt', key: 'issue_weight', sortable: true, numeric: true },
    { label: 'Net Weight', key: 'net_weight_bip', sortable: false, numeric: true },
    { label: 'Pure Weight', key: 'pure_weight_calc', sortable: false, numeric: true },
    { label: 'BIP Stone Wt', key: 'bip_stone_weight', sortable: true, numeric: true },
    { label: 'Removal Wt', key: 'removal_weight', sortable: true, numeric: true },
    { label: 'Remarks', key: 'remarks', sortable: false },
    { label: 'FD', key: 'factory_days', sortable: true, numeric: true },
    { label: 'Stone Qty', key: 'stone_qty', sortable: true, numeric: true },
    { label: 'Dia Qty', key: 'dia_qty', sortable: true, numeric: true },
    { label: 'Dia Broken', key: 'dia_broken', sortable: true, numeric: true },
    { label: 'Color Stone Qty', key: 'color_stone_qty', sortable: true, numeric: true },
    { label: 'Color Stone Broken', key: 'color_stone_broken', sortable: true, numeric: true },
    { label: 'Nick Plate Qty', key: 'nick_plate_qty', sortable: true, numeric: true },
    { label: 'Nick Plate Broken', key: 'nick_plate_broken', sortable: true, numeric: true },
    { label: 'Pearl Weight', key: 'pearl_weight', sortable: true, numeric: true },
    { label: 'Pearl Qty', key: 'pearl_qty', sortable: true, numeric: true },
    { label: 'Warehouse', key: 'warehouse', sortable: true },
    { label: 'JO No', key: 'jo_number', sortable: true },
    { label: 'JO Date', key: 'jo_date', sortable: true },
    { label: 'Prod Route', key: 'production_route', sortable: true },
    { label: 'Created By', key: 'created_by', sortable: true },
    { label: 'Created At', key: 'created_at', sortable: true },
    { label: 'Processed At', key: 'processed_at', sortable: true },
];

const DepartmentWisePendingReportTable = ({ reportData, loading }) => {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('batch_number');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sortedData = useMemo(() => {
        if (!reportData || reportData.length === 0) return [];
        
        return [...reportData].sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];
            
            // Handle numeric values
            if (columns.find(col => col.key === orderBy)?.numeric) {
                aValue = parseFloat(aValue) || 0;
                bValue = parseFloat(bValue) || 0;
            } else {
                // Handle string values
                aValue = String(aValue || '').toLowerCase();
                bValue = String(bValue || '').toLowerCase();
            }
            
            if (order === 'desc') {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            } else {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            }
        });
    }, [reportData, order, orderBy]);

    const paginatedData = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return sortedData.slice(startIndex, startIndex + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const createSortHandler = (property) => (event) => {
        handleRequestSort(property);
    };

    const getStatusChip = (status) => {
        const statusValue = (status || '').trim();
        if (statusValue === 'Unscanned') {
            return <Chip label="Unscanned" color="warning" size="small" />;
        } else if (statusValue === 'Employee Pending') {
            return <Chip label="Employee Pending" color="primary" size="small" />;
        } else if (statusValue === 'Department Pending') {
            return <Chip label="Department Pending" color="secondary" size="small" />;
        } else if (statusValue === 'Processed') {
            return <Chip label="Processed" color="success" size="small" />;
        } else {
            // Default fallback
            return <Chip label={statusValue || 'Unknown'} color="default" size="small" />;
        }
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    align={col.numeric ? 'right' : 'left'}
                                    sortDirection={orderBy === col.key ? order : false}
                                >
                                    {col.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === col.key}
                                            direction={orderBy === col.key ? order : 'asc'}
                                            onClick={createSortHandler(col.key)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    ) : (
                                        col.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                        <Typography sx={{ ml: 2 }}>Loading...</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : paginatedData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <Typography sx={{ py: 2 }}>No data found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((row, idx) => (
                                <TableRow key={idx} sx={{ 
                                    backgroundColor: row.status === 'Unscanned' ? '#fff3e0' : 'inherit'
                                }}>
                                    {columns.map((col) => {
                                        let cellStyle = {};
                                        if (col.key === 'department_name' || col.key === 'worker_name') {
                                            cellStyle.backgroundColor = '#e3f2fd'; // light blue
                                        } else if (col.key === 'received_weight' || col.key === 'issue_weight') {
                                            cellStyle.backgroundColor = '#fffde7'; // light yellow
                                        } else if (col.key === 'net_weight_bip' || col.key === 'pure_weight_calc') {
                                            cellStyle.backgroundColor = '#e8f5e9'; // light green
                                        } else if (col.key === 'bip_stone_weight' || col.key === 'removal_weight') {
                                            cellStyle.backgroundColor = '#fff3e0'; // light orange
                                        }
                                        if (col.key === 'status') {
                                            return <TableCell key={col.key} align={col.numeric ? 'right' : 'left'} sx={cellStyle}>{getStatusChip(row[col.key])}</TableCell>;
                                        } else if (col.key === 'jo_date' || col.key === 'created_at' || col.key === 'processed_at') {
                                            return <TableCell key={col.key} align={col.numeric ? 'right' : 'left'} sx={cellStyle}>{row[col.key] ? new Date(row[col.key]).toLocaleString() : '-'}</TableCell>;
                                        } else if (col.key === 'net_weight_bip') {
                                            return <TableCell key={col.key} align="right" sx={cellStyle}>{row.net_weight !== undefined && row.net_weight !== null ? parseFloat(row.net_weight).toFixed(3) : '-'}</TableCell>;
                                        } else if (col.key === 'pure_weight_calc') {
                                            const netWeight = parseFloat(row.net_weight) || 0;
                                            const purity = parseFloat(row.hm_purity) || 0;
                                            const pureWeight = (netWeight * purity / 100).toFixed(3);
                                            return <TableCell key={col.key} align="right" sx={cellStyle}>{pureWeight}</TableCell>;
                                        } else if ([
                                            'stone_qty', 'dia_qty', 'dia_broken', 'color_stone_qty', 'color_stone_broken',
                                            'nick_plate_qty', 'nick_plate_broken', 'pearl_weight', 'pearl_qty'
                                        ].includes(col.key)) {
                                            const value = row[col.key];
                                            return <TableCell key={col.key} align="right" sx={cellStyle}>{value !== undefined && value !== null ? parseFloat(value).toFixed(3) : '-'}</TableCell>;
                                        } else if (col.numeric && row[col.key]) {
                                            return <TableCell key={col.key} align="right" sx={cellStyle}>{parseFloat(row[col.key]).toFixed(3)}</TableCell>;
                                        } else {
                                            return <TableCell key={col.key} align={col.numeric ? 'right' : 'left'} sx={cellStyle}>{row[col.key] || '-'}</TableCell>;
                                        }
                                    })}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={reportData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
            />
        </Paper>
    );
};

export default DepartmentWisePendingReportTable; 